import { useMemo } from "react";
import type { MarketPair } from "@cede/types";
import { API_ENDPOINT, isDexId } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useQuery } from "@tanstack/react-query";

// Constant empty array to avoid creating new references
const EMPTY_MARKETS: MarketPair[] = [];

/**
 * Reusable hook to fetch market pairs for a CEX exchange
 * Shared cache with trade widget to avoid duplicate requests
 *
 * Market pairs are public data - the backend endpoint only uses exchangeId
 * and returns the same data regardless of authentication.
 *
 * @param exchangeId - The exchange ID (e.g., "binance", "kucoin")
 * @param accountId - Optional account ID for cache key differentiation (not sent to backend)
 * @returns Markets data and loading state
 */
export const useMarkets = (exchangeId?: string, accountId?: string) => {
  const apiHelper = useApi();

  // DEX exchangeIds don't resolve to a CCXT instance on the BE,
  // so the public market-pairs endpoint 500s. Skip the call.
  const isFetchable = exchangeId ? !isDexId(exchangeId) : false;

  const { data: markets, isLoading } = useQuery({
    queryKey: ["marketPairs", exchangeId, accountId],
    queryFn: async () => {
      if (!exchangeId) return EMPTY_MARKETS;

      // Market pairs are public data - use public endpoint
      // Backend only accepts exchangeId, accountId is ignored
      const resMarkets: MarketPair[] = await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_MARKET_PAIRS, {
        queryParams: { exchangeId },
      });
      return resMarkets;
    },
    enabled: isFetchable,
    staleTime: 5 * 60 * 1000, // 5 minutes - markets don't change often
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Return the data directly if it exists, otherwise return the constant empty array
  // This prevents creating new array references on every render
  return useMemo(
    () => ({
      markets: markets ?? EMPTY_MARKETS,
      isLoading,
    }),
    [markets, isLoading],
  );
};
